import React from 'react';

export interface FileUploadProgressProps {
  progress: number;
  fileName: string;
  onCancel: () => void;
  status?: 'uploading' | 'completed' | 'error';
  error?: string;
  disabled?: boolean;
  size?: 'default' | 'compact';
  className?: string;
  animated?: boolean;
}

export const FileUploadProgress: React.FC<FileUploadProgressProps> = ({
  progress,
  fileName,
  onCancel,
  status = 'uploading',
  error,
  disabled = false,
  size = 'default',
  className = '',
  animated = true
}) => {
  // プログレス値を0-100の範囲にクランプし、整数に丸める
  const clampedProgress = Math.max(0, Math.min(100, Math.round(progress)));

  // CSS クラス名を構築（FileUploadProgress.scss から内製化 / Wave 3d T20-batch3a）。
  // compact は元 SCSS で子孫セレクタによる上書きだったため、各要素側で出し分ける。
  // Tailwind の purge は文字列連結を追えないので、状態ごとに完全な文字列リテラルを持つ。
  const isCompact = size === 'compact';

  const containerClass = [
    isCompact
      ? 'p-3 border border-[#e1e5e9] rounded-lg bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] mb-2'
      : 'p-4 border border-[#e1e5e9] rounded-lg bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] mb-2',
    className
  ].filter(Boolean).join(' ');

  const headerClass = isCompact
    ? 'flex justify-between items-center mb-2'
    : 'flex justify-between items-center mb-3';

  const filenameClass = isCompact
    ? 'font-semibold text-[#2d3748] text-[14px] break-all flex-1 mr-3'
    : 'font-semibold text-[#2d3748] text-[16px] break-all flex-1 mr-3';

  const cancelBtnClass = isCompact
    ? 'bg-[#f7fafc] border border-[#e2e8f0] rounded-md px-2 py-1 text-[12px] text-[#4a5568] cursor-pointer transition-all duration-200 ease-[ease] [&:hover:not(:disabled)]:bg-[#edf2f7] [&:hover:not(:disabled)]:border-[#cbd5e0] disabled:opacity-50 disabled:cursor-not-allowed'
    : 'bg-[#f7fafc] border border-[#e2e8f0] rounded-md px-3 py-[6px] text-[14px] text-[#4a5568] cursor-pointer transition-all duration-200 ease-[ease] [&:hover:not(:disabled)]:bg-[#edf2f7] [&:hover:not(:disabled)]:border-[#cbd5e0] disabled:opacity-50 disabled:cursor-not-allowed';

  // progress-pulse の keyframes は globals.css に fup-progress-pulse として移設
  const progressFillClass = animated
    ? 'h-full bg-gradient-to-r from-[#3182ce] to-[#4299e1] rounded transition-[width] duration-300 ease-[ease] animate-[fup-progress-pulse_2s_ease-in-out_infinite]'
    : 'h-full bg-gradient-to-r from-[#3182ce] to-[#4299e1] rounded transition-[width] duration-300 ease-[ease]';

  // ステータス表示テキスト
  const getStatusText = () => {
    switch (status) {
      case 'uploading':
        return 'アップロード中';
      case 'completed':
        return '完了';
      case 'error':
        return 'エラー';
      default:
        return 'アップロード中';
    }
  };

  // プログレス更新の live region テキスト
  const getLiveRegionText = () => {
    return `${clampedProgress}%`;
  };

  return (
    <div 
      className={containerClass}
      data-testid="file-upload-progress"
    >
      {/* ファイル名表示 */}
      <div className={headerClass}>
        <span className={filenameClass}>
          {fileName}
        </span>
        <button
          type="button"
          className={cancelBtnClass}
          onClick={onCancel}
          disabled={disabled}
          aria-label="アップロードをキャンセル"
        >
          キャンセル
        </button>
      </div>

      {/* プログレスバー */}
      <div className="flex items-center gap-3 mb-2">
        <div
          className="flex-1 h-2 bg-[#e2e8f0] rounded relative overflow-hidden"
          role="progressbar"
          aria-label="ファイルアップロード進捗"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={progressFillClass}
            data-testid="progress-fill"
            style={{ width: `${clampedProgress}%` }}
          />
        </div>
        <span className="text-[14px] font-semibold text-[#4a5568] min-w-[40px] text-right">
          {clampedProgress}%
        </span>
      </div>

      {/* ステータス表示 */}
      <div className="flex flex-col gap-1">
        <span className="text-[14px] text-[#718096]">
          {getStatusText()}
        </span>
        {status === 'error' && error && (
          <div
            className="text-[13px] text-[#e53e3e] bg-[#fed7d7] px-2 py-[6px] rounded border-l-[3px] border-l-[#e53e3e]"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>

      {/* アクセシビリティ用のライブリージョン */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
      >
        {getLiveRegionText()}
      </div>
    </div>
  );
};